import { useCallback, useEffect, useMemo } from 'react';
import { ShopCategoriesGrid } from '~/components/ui/Shop';
import {
	ShopCategory,
	useShopCategoryControls,
} from '~/components/ui/ShopCategory';
import { rage } from '~/rage';
import { Gender } from '~/shared';
import {
	setActiveCategory,
	setActiveStage,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { categories } from './CategoryTab.model';

const CategoryTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const tabIndex = useLayoutSelector((state) => state.clothesShop.tabIndex);
	const shopItems = useLayoutSelector((state) => state.clothesShop.shopItems);
	const playerGender = useLayoutSelector(
		(state) => state.clothesShop.playerGender,
	);

	const filteredCategories = useMemo(() => {
		return categories.filter((category) => {
			const categoryItems = shopItems.filter(
				(item) =>
					item.category === category.type &&
					(item.gender === playerGender || item.gender === Gender.COMMON),
			);

			return categoryItems.length > 0;
		});
	}, [shopItems, playerGender]);

	const categoryUnseenMap = useMemo(() => {
		const map: Record<number, boolean> = {};

		for (const category of filteredCategories) {
			const categoryItems = shopItems.filter(
				(item) =>
					item.category === category.type &&
					item.parentItemId === null &&
					(item.gender === playerGender || item.gender === Gender.COMMON),
			);

			for (const item of categoryItems) {
				const seenTrackerKey = `${tabIndex}.${item.category}.${item.id}`;
				if (rage.clothesShop.isUnseen(seenTrackerKey)) {
					map[category.type] = true;
					break;
				}
			}
		}

		return map;
	}, [filteredCategories, shopItems, playerGender, tabIndex]);

	const handleSelect = useCallback(
		(index: number) => {
			if (filteredCategories?.[index] === undefined) {
				return;
			}

			dispatch(setActiveStage(StageTypes.List));
			dispatch(setActiveCategory(filteredCategories[index].type));
		},
		[filteredCategories],
	);

	const { setMaxCategoryIndex, activeCategoryIndex, setActiveCategoryIndex } =
		useShopCategoryControls(null, handleSelect);

	useEffect(() => {
		if (activeCategoryIndex === null) {
			dispatch(setActiveCategory(null));
			return;
		}

		if (
			activeCategoryIndex >= 0 &&
			activeCategoryIndex < filteredCategories.length
		) {
			dispatch(setActiveCategory(filteredCategories[activeCategoryIndex].type));
		}
	}, [activeCategoryIndex, filteredCategories]);

	useEffect(() => {
		setMaxCategoryIndex(filteredCategories.length);
	}, [filteredCategories]);

	if (filteredCategories.length === 0) {
		return null;
	}

	return (
		<ShopCategoriesGrid style={{ marginTop: 30 }}>
			{filteredCategories.map((item, index) => {
				const seenTrackerKey = `${tabIndex}.${item?.type}`;

				return (
					<ShopCategory
						name={item?.name || ''}
						icon={item?.image || ''}
						onClick={() => handleSelect(index)}
						isActive={activeCategoryIndex === index}
						isNew={
							categoryUnseenMap[item?.type] ??
							rage.clothesShop.isUnseen(seenTrackerKey)
						}
						key={index}
						onMouseEnter={() => setActiveCategoryIndex(index)}
						onMouseLeave={() => setActiveCategoryIndex(null)}
					/>
				);
			})}
		</ShopCategoriesGrid>
	);
};

export { CategoryTab };
